<template>
    <component
        :is="wrapperTag"
        class="s-ribbon"
        :class="className"
        v-bind="wrapperProps"
    >
        <a-picture
            v-if="normalizedBackgroundImage"
            class="background"
            :background="normalizedBackgroundImage"
            :alt="backgroundImage?.alt || labelPrimary || labelAuxiliary"
            fit="cover"
            is-eager
            is-background
        />
        <div class="a-container content">
            <v-clamp
                v-if="labelPrimary"
                class="label primary"
                tag="div"
                :max-lines="2"
                :autoresize="true"
                @clampchange="onClampChange($event, 'first')"
            >
                {{ labelPrimary }}
            </v-clamp>
            <v-clamp
                v-if="labelAuxiliary"
                class="label secondary"
                tag="div"
                :max-lines="2"
                :autoresize="true"
                @clampchange="onClampChange($event, 'second')"
            >
                {{ labelAuxiliary }}
            </v-clamp>
            <a-glyph
                v-if="showStandaloneIcon"
                :class="iconPosition"
                :name="iconName"
                size="m"
                class="icon"
            />
        </div>
    </component>
</template>

<script>
import VClamp from 'vue3-clamp';
import DynamicLink from '@core/components/dynamic-link/dynamic-link.vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import APicture from '@core/components/picture/picture.vue';
import {
    getRibbonTarget,
    normalizeRibbonBackgroundImage,
} from './utils.js';

export default {
    name: 'SRibbon',
    components: {
        APicture,
        AGlyph,
        VClamp,
        DynamicLink,
    },
    props: {
        labelPrimary: {
            type: String,
            default: '',
        },
        labelAuxiliary: {
            type: String,
            default: null,
        },
        linkUrl: {
            type: String,
            default: null,
        },
        iconPosition: {
            type: String,
            default: 'start',
        },
        iconName: {
            type: String,
            default: null,
        },
        styleName: {
            type: String,
            default: 'blue',
        },
        backgroundImage: {
            type: Object,
            default: undefined,
        },
        backgroundImageMobileId: {
            type: String,
            default: null,
        },
        backgroundImageTabletId: {
            type: String,
            default: null,
        },
        backgroundImageDesktopId: {
            type: String,
            default: null,
        },
    },
    data: () => ({
        isLabelClamped: {
            first: true,
            second: true,
        },
    }),
    computed: {
        className() {
            const firstLabelClamped = this.labelPrimary && this.isLabelClamped.first;
            const secondLabelClamped = this.labelAuxiliary && this.isLabelClamped.second;

            return {
                [this.styleName]: true,
                'is-link': this.isWrappedWithLink,
                'is-clamped': firstLabelClamped || secondLabelClamped,
                'has-background-image': Boolean(this.normalizedBackgroundImage),
            };
        },
        isClickable() {
            return Boolean(this.linkUrl);
        },
        isWrappedWithLink() {
            return this.isClickable;
        },
        wrapperTag() {
            return this.isWrappedWithLink ? DynamicLink : 'div';
        },
        wrapperProps() {
            if (!this.isWrappedWithLink) return {};

            return {
                to: this.linkUrl,
                target: this.linkTarget,
                utmWebteam: 'all-ribbon',
                event: {
                    location: 'top ribbon',
                    content: this.labelPrimary,
                },
            };
        },
        linkTarget() {
            return getRibbonTarget(this.linkUrl);
        },
        showStandaloneIcon() {
            return Boolean(this.iconName);
        },
        normalizedBackgroundImage() {
            return normalizeRibbonBackgroundImage(this);
        },
    },
    methods: {
        onClampChange(clamped, label) {
            this.isLabelClamped[label] = clamped;
        },
    },
};
</script>

<style lang="pcss" scoped>
.s-ribbon {
    position: relative;
    z-index: 401; /* to be above s-main-header */
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    transition: background-color linear 0.2s;
    min-height: 48px;
    flex-shrink: 0;
    overflow: hidden;

    @media (--viewport-tablet) {
        min-height: 56px;
    }

    .background {
        position: absolute;
        inset: 0;
        z-index: 0;
    }

    &:focus {
            outline: 3px solid var(--av-inversed-lighter);
            outline-offset: -3px;
        }

    .content {
        @mixin caption-accent;
        position: relative;
        z-index: 2;
        width: 100%;
        color: var(--av-nav-primary);
        transition: background linear 0.2s;
        column-gap: 16px;
        padding-block: 8px;

        @media (--viewport-tablet) {
            padding-block: 12px;
            flex-direction: row;
            align-items: center;
            display: flex;
            justify-content: center;
        }

        @media (--viewport-desktop) {
            @mixin body-accent;
        }

        @media (--viewport-desktop-large) {
            padding-inline: 0;
        }
    }

    .label {
        margin-inline-end: 2px;

        &.primary {
            order: 2;
            margin-inline-start: 0;
        }

        &.secondary {
            order: 4;
        }
    }

    .icon {
        fill: var(--av-nav-primary);
        display: none;
        flex-shrink: 0;
        margin: 0;
        max-width: 24px;
        max-height: 24px;

        @media (--viewport-tablet) {
            display: block;
        }

        &.start {
            order: 1;
        }

        &.divider {
            order: 3;
        }

        &.end {
            margin-inline-end: 0;
            order: 5;
        }
    }

    &.yellow {
        background: var(--av-fixed-warning-light);
        &:hover {
            background: var(--av-fixed-warning);
        }
    }

    &.blue {
        background: var(--av-nav-secondary);
        &:hover {
            background: var(--av-fixed-info-dark);
        }
    }

    &.ai-purple {
        background: linear-gradient(75deg, #1F379C 0%, #3849E0 50%, #FC2DF1 100%);
    }

    &.gradient {
        background: linear-gradient(90.05deg, #05245A 3.92%, #2C40C0 44.27%, #EC0004 99.99%);
        &:hover {
            background: linear-gradient(90.05deg, #05245A 26.02%, #2C40C0 67.81%, #B2133D 99.99%);
        }
    }

    &.dark-blue,
    &.navy {
        background: var(--av-nav-primary);
        &:hover {
            background: var(--av-primary-hover);
        }
    }

    &.green,
    &.light-green {
        background: var(--av-fixed-success-light);
        &:hover {
            background: var(--av-fixed-success);
        }
    }

    &.light-blue {
        background: var(--av-fixed-info-accent);
        &:hover {
            background: var(--av-fixed-info-light);
        }
        &:focus {
            outline: 3px solid var(--av-brand-secondary-light);
            outline-offset: -3px;
        }
    }

    &.red {
        background: rgba(193, 43, 34, 1);
        &:hover {
            background: var(--av-fixed-danger);
        }
    }

    &.mid-blue {
        background: rgba(33, 89, 209, 1);
        &:hover {
            background: var(--av-fixed-info-dark);
        }
    }

    &.ai-gradient {
        background: linear-gradient(75deg, #1F379C 0%, #3849E0 50%, #FC2DF1 100%);
        &:hover {
            background: linear-gradient(75deg, #142FA0 20%, #E90DDD 100%);
        }
    }

    &.blue,
    &.ai-purple,
    &.dark-blue,
    &.navy,
    &.red,
    &.mid-blue,
    &.gradient,
    &.ai-gradient {
        .content,
        .icon {
            color: var(--av-inversed-primary);
            fill: var(--av-inversed-primary);
        }
    }

    &.is-clamped {
        .label {
            flex: 1 0 0;
        }
    }

    &.is-link {
        cursor: pointer;
    }
}
</style>
